import { createFileRoute, Link, Outlet, useLocation } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth-context";
import { Button } from "@/components/ui/button";
import { FileText, Users, Layers, Tag, Settings, FileCheck, LogOut, ScrollText, UserCog } from "lucide-react";
import logo from "@/assets/logo.png";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin")({
  component: AdminLayout,
});

const nav = [
  { to: "/admin", label: "En attente", icon: FileText, exact: true },
  { to: "/admin/invoices", label: "Factures", icon: FileCheck },
  { to: "/admin/clients", label: "Clients", icon: Users },
  { to: "/admin/materials", label: "Matières", icon: Layers },
  { to: "/admin/pricing", label: "Tarifs", icon: Tag },
  { to: "/admin/users", label: "Utilisateurs", icon: UserCog },
  { to: "/admin/logs", label: "Journal", icon: ScrollText },
  { to: "/admin/settings", label: "Paramètres", icon: Settings },
];

function AdminLayout() {
  const { signOut, isAdmin, user } = useAuth();
  const loc = useLocation();

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-xl font-semibold">Accès restreint</h1>
          <p className="text-sm text-muted-foreground">
            Votre compte ({user?.email}) n'a pas le rôle administrateur.
          </p>
          <p className="text-xs text-muted-foreground">
            Pour devenir admin, demandez-moi d'ajouter votre email comme administrateur dans la base.
          </p>
          <Button variant="outline" onClick={signOut}>Se déconnecter</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-background">
      <aside className="w-60 bg-gray-900 text-white flex flex-col">
        {/* Logo */}
        <div className="p-6 border-b border-gray-800">
          <img src={logo} alt="LocMat" className="h-8 w-auto" />
        </div>
        
        {/* Navigation */}
        <nav className="flex-1 px-3 py-4 space-y-1">
          {nav.map(item => {
            const active = item.exact ? loc.pathname === item.to : loc.pathname.startsWith(item.to);
            return (
              <Link
                key={item.to}
                to={item.to}
                className={cn(
                  "flex items-center gap-3 rounded-md px-3 py-2.5 text-sm transition-colors",
                  active
                    ? "bg-gray-800 text-white"
                    : "text-gray-300 hover:bg-gray-800 hover:text-white"
                )}
              >
                <item.icon className="h-5 w-5" />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        
        {/* User info & logout */}
        <div className="p-3 border-t border-gray-800">
          <div className="px-3 py-2 text-xs text-gray-400 truncate mb-2">
            {user?.email}
          </div>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-sm text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
          >
            <LogOut className="h-5 w-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>
      
      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
